import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db as defaultDb } from '../Components/auth/firebaseConfig';

/*
 * Reusable tenant + pricing/licensing module.
 *
 * This module intentionally uses the current app Firestore database. It does
 * not create or depend on a separate central tenant database yet. Future apps
 * can copy this file, change the collection names/action costs, or inject a
 * different Firestore instance through the options argument.
 *
 * App-specific work should stay outside this module. For example, OVplan
 * creates a project only after deductCreditsFromTenant succeeds.
 */

export const BILLING_CYCLE_DAYS = 30;
export const ROLLOVER_WINDOW_DAYS = 40;
export const APP_ID = 'project-management-v1';
export const JWT_REFRESH_THRESHOLD = 2 * 60 * 60 * 1000;

export const TENANT_PRICING_COLLECTIONS = {
  tenants: 'tenant',
  users: 'users',
  userGroups: 'userGroups',
  pricingPlans: 'pricingPlans',
  usageHistory: 'tenantCreditUsage',
};

export const FAILURE_REASONS = {
  INVALID_TENANT: 'INVALID_TENANT',
  TENANT_INACTIVE: 'TENANT_INACTIVE',
  INVALID_PLAN: 'INVALID_PLAN',
  ACTION_NOT_ALLOWED: 'ACTION_NOT_ALLOWED',
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  USER_LIMIT_REACHED: 'USER_LIMIT_REACHED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
};

export const PLANS = {
  Free: { credits: 20, maxUsers: 2, label: 'Free', price: 0, priceSuffix: 'forever', color: '#78909c', popular: false },
  Trial: { credits: 50, maxUsers: 3, label: 'Trial', price: 0, priceSuffix: '14 days', color: '#90a4ae', popular: false },
  Starter: { credits: 200, maxUsers: 5, label: 'Starter', price: 199, priceSuffix: 'per month', color: '#42a5f5', popular: false },
  Basic: { credits: 500, maxUsers: 10, label: 'Basic', price: 499, priceSuffix: 'per month', color: '#1976d2', popular: false },
  Professional: { credits: 1500, maxUsers: 25, label: 'Professional', price: 1499, priceSuffix: 'per month', color: '#7b1fa2', popular: true },
  Growth: { credits: 3000, maxUsers: 50, label: 'Growth', price: 2999, priceSuffix: 'per month', color: '#2e7d32', popular: false },
  Business: { credits: 7500, maxUsers: 150, label: 'Business', price: 6999, priceSuffix: 'per month', color: '#e65100', popular: false },
  Enterprise: { credits: null, maxUsers: null, label: 'Enterprise', price: null, priceSuffix: 'custom', color: '#f57c00', popular: false },
};

export const PLAN_ORDER = ['Free', 'Trial', 'Starter', 'Basic', 'Professional', 'Growth', 'Business', 'Enterprise'];

export const DEFAULT_CREDIT_ACTIONS = {
  createProject: 10,
  createBoard: 5,
  createSprintBoard: 5,
  addCard: 1,
  // Backward-compatible aliases used by the current OVplan code.
  project: 10,
  board: 5,
  sprint_board: 5,
  card: 1,
};

export const CREDIT_COSTS = DEFAULT_CREDIT_ACTIONS;

let configuredCollections = { ...TENANT_PRICING_COLLECTIONS };
let configuredCreditActions = { ...DEFAULT_CREDIT_ACTIONS };

const nowIso = () => new Date().toISOString();

const successResult = (payload = {}) => ({ success: true, ...payload });
const failureResult = (reason, payload = {}) => ({ success: false, reason, ...payload });

const getModuleDb = (options = {}) => options.db || defaultDb;

const getCollections = (options = {}) => ({
  ...configuredCollections,
  ...(options.collectionNames || {}),
});

const getCreditActions = (options = {}) => ({
  ...configuredCreditActions,
  ...(options.creditActions || {}),
});

export const setTenantPricingCollectionNames = (overrides = {}) => {
  configuredCollections = {
    ...configuredCollections,
    ...Object.fromEntries(
      Object.entries(overrides).filter(([, value]) => typeof value === 'string' && value.trim())
    ),
  };
  return { ...configuredCollections };
};

export const getTenantPricingCollectionNames = () => ({ ...configuredCollections });

const getNextTenantSourceVersion = (tenant = {}) => {
  const current = Number(tenant.sourceVersion ?? tenant.license?.syncVersion ?? 0);
  return current + 1;
};

export const setCreditActionCosts = (actionCosts = {}) => {
  configuredCreditActions = {
    ...configuredCreditActions,
    ...Object.fromEntries(
      Object.entries(actionCosts)
        .filter(([, value]) => Number.isFinite(Number(value)) && Number(value) >= 0)
        .map(([key, value]) => [key, Number(value)])
    ),
  };
  return { ...configuredCreditActions };
};

export const getCreditActionCosts = () => ({ ...configuredCreditActions });

function normalizePlanConfig(planName, planConfig = {}) {
  return {
    name: planName,
    label: planConfig.label || planConfig.name || planName,
    price: Number(planConfig.price ?? 0),
    credits: planConfig.credits === null ? null : Number(planConfig.credits ?? 0),
    maxUsers: planConfig.maxUsers === null ? null : Number(planConfig.maxUsers ?? 1),
    features: Array.isArray(planConfig.features) ? planConfig.features : [],
    actionCosts: planConfig.actionCosts || {},
    color: planConfig.color || '#1976d2',
    popular: Boolean(planConfig.popular),
    priceSuffix: planConfig.priceSuffix || 'per month',
  };
}

export function getDefaultLicense() {
  return buildLicenseForPlan('Trial');
}

export function buildLicenseForPlan(planName, options = {}) {
  const now = new Date();
  const cycleEnd = new Date(now);
  cycleEnd.setDate(cycleEnd.getDate() + BILLING_CYCLE_DAYS);
  const planConfig = PLANS[planName] || PLANS.Trial;
  const creditsTotal = options.creditsTotal ?? planConfig.credits ?? PLANS.Trial.credits;
  const userLimit = options.userLimit ?? planConfig.maxUsers ?? PLANS.Trial.maxUsers;

  return {
    plan: planName,
    userLimit,
    currentCycle: {
      creditsTotal,
      creditsUsed: 0,
      cycleStart: now.toISOString(),
      cycleEnd: cycleEnd.toISOString(),
    },
    rollover: {
      creditsAvailable: 0,
      creditsUsed: 0,
      expiresAt: null,
      isActive: false,
    },
    licenseJWT: null,
    jwtIssuedAt: null,
    jwtExpiresAt: null,
    lastSyncedAt: now.toISOString(),
    syncVersion: 0,
    planAssignedAt: now.toISOString(),
    planAssignedBy: options.planAssignedBy || 'system',
  };
}

export function buildLicenseForPlanConfig(planName, planConfig = {}, options = {}) {
  const normalizedPlan = normalizePlanConfig(planName, planConfig);
  return buildLicenseForPlan(planName, {
    planAssignedBy: options.planAssignedBy || options.actor || 'system',
    creditsTotal: normalizedPlan.credits ?? PLANS.Trial.credits,
    userLimit: normalizedPlan.maxUsers ?? PLANS.Trial.maxUsers,
  });
}

export function getPlanNameFromTenant(tenant, fallbackPlan = 'Trial') {
  return tenant?.license?.plan || tenant?.planId || tenant?.plan || fallbackPlan;
}

export function getLicenseForTenant(tenant, fallbackPlan = 'Trial') {
  if (!tenant) return null;
  const planName = getPlanNameFromTenant(tenant, fallbackPlan);
  return tenant.license || buildLicenseForPlan(planName, { planAssignedBy: 'system-fallback' });
}

export function computeActiveUserLimit(license, fallbackLimit = PLANS.Trial.maxUsers) {
  if (!license) return fallbackLimit;
  const grants = Array.isArray(license.userLimitGrants) ? license.userLimitGrants : [];
  if (!grants.length && license.baseUserLimit === undefined) {
    return license.userLimit ?? fallbackLimit;
  }
  const baseUserLimit = Number(license.baseUserLimit ?? license.userLimit ?? fallbackLimit);
  const activeGrantSeats = grants.reduce((total, grant) => {
    const expiresAt = grant?.expiresAt ? new Date(grant.expiresAt).getTime() : Number.POSITIVE_INFINITY;
    return Number(grant?.seats || 0) > 0 && expiresAt > Date.now()
      ? total + Number(grant.seats || 0)
      : total;
  }, 0);
  return Math.max(0, baseUserLimit) + activeGrantSeats;
}

export function isCycleExpired(license) {
  const cycleEnd = license?.currentCycle?.cycleEnd;
  if (!cycleEnd) return false;
  return Date.now() > new Date(cycleEnd).getTime();
}

export function isRolloverExpired(license) {
  if (!license?.rollover?.isActive) return true;
  const expiresAt = license?.rollover?.expiresAt;
  if (!expiresAt) return true;
  return Date.now() > new Date(expiresAt).getTime();
}

export function computeRolloverRemaining(license) {
  if (!license?.rollover?.isActive || isRolloverExpired(license)) return 0;
  const remaining = (license.rollover.creditsAvailable || 0) - (license.rollover.creditsUsed || 0);
  return Math.max(0, remaining);
}

export function computeCreditsRemaining(license) {
  const rolloverLeft = computeRolloverRemaining(license);
  const cycleLeft = Math.max(
    0,
    (license?.currentCycle?.creditsTotal || 0) - (license?.currentCycle?.creditsUsed || 0)
  );
  return rolloverLeft + cycleLeft;
}

export function isUserLimitReached(license, currentUserCount) {
  return currentUserCount >= computeActiveUserLimit(license);
}

export function renewLicenseCycle(license, now = new Date()) {
  const cycleEnd = new Date(now);
  cycleEnd.setDate(cycleEnd.getDate() + BILLING_CYCLE_DAYS);

  const rolloverExpiry = new Date(cycleEnd);
  rolloverExpiry.setDate(rolloverExpiry.getDate() + ROLLOVER_WINDOW_DAYS);

  const currentCycleRemaining = Math.max(
    0,
    (license?.currentCycle?.creditsTotal || 0) - (license?.currentCycle?.creditsUsed || 0)
  );
  const existingRolloverRemaining = computeRolloverRemaining(license);
  const nextRolloverCredits = currentCycleRemaining + existingRolloverRemaining;

  return {
    ...license,
    currentCycle: {
      creditsTotal: license?.currentCycle?.creditsTotal || PLANS.Trial.credits,
      creditsUsed: 0,
      cycleStart: now.toISOString(),
      cycleEnd: cycleEnd.toISOString(),
    },
    rollover: {
      creditsAvailable: nextRolloverCredits,
      creditsUsed: 0,
      expiresAt: nextRolloverCredits > 0 ? rolloverExpiry.toISOString() : null,
      isActive: nextRolloverCredits > 0,
    },
    lastSyncedAt: now.toISOString(),
    syncVersion: (license?.syncVersion || 0) + 1,
  };
}

export function buildPlanChangeLicense(tenant, nextPlan, options = {}) {
  const existingLicense = getLicenseForTenant(tenant, nextPlan);
  const planLicense = buildLicenseForPlan(nextPlan, {
    planAssignedBy: options.actor || 'system',
  });
  const nextTotal = planLicense.currentCycle?.creditsTotal || 0;
  const existingUsed = existingLicense?.currentCycle?.creditsUsed || 0;

  return {
    ...existingLicense,
    plan: nextPlan,
    userLimit: planLicense.userLimit,
    currentCycle: {
      ...(existingLicense?.currentCycle || {}),
      creditsTotal: nextTotal,
      creditsUsed: Math.min(existingUsed, nextTotal),
      cycleStart: existingLicense?.currentCycle?.cycleStart || planLicense.currentCycle.cycleStart,
      cycleEnd: existingLicense?.currentCycle?.cycleEnd || planLicense.currentCycle.cycleEnd,
    },
    rollover: existingLicense?.rollover || planLicense.rollover,
    lastSyncedAt: nowIso(),
    syncVersion: (existingLicense?.syncVersion || 0) + 1,
    planAssignedAt: nowIso(),
    planAssignedBy: options.actor || 'system',
  };
}

export function getTenantCreditBalance(tenantOrLicense) {
  const license = tenantOrLicense?.currentCycle ? tenantOrLicense : getLicenseForTenant(tenantOrLicense);
  const cycleTotal = license?.currentCycle?.creditsTotal || 0;
  const cycleUsed = license?.currentCycle?.creditsUsed || 0;
  const cycleRemaining = Math.max(0, cycleTotal - cycleUsed);
  const rolloverRemaining = computeRolloverRemaining(license);

  return {
    plan: license?.plan || 'Trial',
    totalCredits: cycleTotal,
    usedCredits: cycleUsed,
    cycleRemainingCredits: cycleRemaining,
    rolloverCredits: rolloverRemaining,
    remainingCredits: cycleRemaining + rolloverRemaining,
    userLimit: computeActiveUserLimit(license),
    cycleEnd: license?.currentCycle?.cycleEnd || null,
    expired: isCycleExpired(license),
  };
}

async function resolveTenantSnapshot(firestore, tenantIdOrDocId, collections) {
  if (!tenantIdOrDocId) return null;

  const directRef = doc(firestore, collections.tenants, tenantIdOrDocId);
  const directSnap = await getDoc(directRef);
  if (directSnap.exists()) return directSnap;

  const tenantQuery = query(
    collection(firestore, collections.tenants),
    where('tenantId', '==', tenantIdOrDocId)
  );
  const querySnapshot = await getDocs(tenantQuery);
  if (querySnapshot.empty) return null;

  return querySnapshot.docs.find((snap) => {
    const data = snap.data() || {};
    return !!(data.license || data.status || data.companyName || data.tenantName);
  }) || querySnapshot.docs[0];
}

export async function resolveTenantDocId(tenantIdOrDocId, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const snap = await resolveTenantSnapshot(firestore, tenantIdOrDocId, collections);
  return snap?.id || null;
}

export async function getTenant(tenantIdOrDocId, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const snap = await resolveTenantSnapshot(firestore, tenantIdOrDocId, collections);
  if (!snap) return null;
  return { id: snap.id, ...snap.data() };
}

export async function getAllTenants(options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const tenantsSnapshot = await getDocs(collection(firestore, collections.tenants));
  return tenantsSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function createTenant(payload = {}, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const plan = payload.plan || payload.licensePlan || options.defaultPlan || 'Professional';
  const now = new Date();
  const tenantPayload = {
    tenantName: payload.tenantName || payload.companyName || '',
    companyName: payload.companyName || '',
    organizationType: payload.organizationType || 'Private',
    domain: payload.domain || '',
    companyAddress: payload.companyAddress || '',
    companyLogoUrl: payload.companyLogoUrl || '',
    personalName: payload.personalName || '',
    personalPhoneNumber: payload.personalPhoneNumber || '',
    plan,
    planId: payload.planId || plan,
    license: payload.license || buildLicenseForPlan(plan, { planAssignedBy: payload.actor || 'system' }),
    status: payload.status || 'active',
    billingStatus: payload.billingStatus || 'active',
    adminAssigned: Boolean(payload.adminAssigned),
    monthlyCredits: payload.monthlyCredits ?? payload.license?.currentCycle?.creditsTotal ?? PLANS[plan]?.credits ?? 0,
    purchasedCredits: payload.purchasedCredits ?? payload.license?.rollover?.creditsAvailable ?? 0,
    usedCredits: payload.usedCredits ?? payload.license?.currentCycle?.creditsUsed ?? 0,
    remainingCredits:
      payload.remainingCredits ??
      getTenantCreditBalance(payload.license || buildLicenseForPlan(plan)).remainingCredits,
    userLimit: payload.userLimit ?? payload.license?.userLimit ?? PLANS[plan]?.maxUsers ?? 0,
    activeUsers: payload.activeUsers ?? 0,
    allowedApps: Array.isArray(payload.allowedApps) ? payload.allowedApps : [],
    sourceVersion: Number(payload.sourceVersion || 1),
    createdAt: payload.createdAt || now,
    updatedAt: payload.updatedAt || now,
  };

  const docRef = await addDoc(collection(firestore, collections.tenants), tenantPayload);
  const tenantId = payload.tenantId || docRef.id;
  await updateDoc(docRef, { tenantId });

  return {
    id: docRef.id,
    tenantId,
    ...tenantPayload,
    tenant: {
      id: docRef.id,
      tenantId,
      ...tenantPayload,
    },
  };
}

export async function updateTenant(tenantIdOrDocId, updates = {}, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const tenantDocId = await resolveTenantDocId(tenantIdOrDocId, options);
  if (!tenantDocId) return failureResult(FAILURE_REASONS.INVALID_TENANT);

  const tenantSnap = await getDoc(doc(firestore, collections.tenants, tenantDocId));
  if (!tenantSnap.exists()) return failureResult(FAILURE_REASONS.INVALID_TENANT);
  const before = { id: tenantSnap.id, ...tenantSnap.data() };

  const payload = {
    ...updates,
    sourceVersion: getNextTenantSourceVersion(before),
    updatedAt: updates.updatedAt || new Date(),
  };

  await updateDoc(doc(firestore, collections.tenants, tenantDocId), payload);
  return successResult({
    tenantId: tenantIdOrDocId,
    tenantDocId,
    before,
    after: { ...before, ...payload },
    tenant: { ...before, ...payload },
    updates: payload,
  });
}

export async function disableTenant(tenantIdOrDocId, options = {}) {
  return updateTenant(tenantIdOrDocId, { status: 'inactive' }, options);
}

export async function setTenantStatus(tenantIdOrDocId, status, options = {}) {
  return updateTenant(tenantIdOrDocId, { status }, options);
}

export async function getPricingPlan(planName, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  try {
    const planSnap = await getDoc(doc(firestore, collections.pricingPlans, planName));
    if (planSnap.exists()) {
      return {
        id: planSnap.id,
        source: 'custom',
        ...normalizePlanConfig(planSnap.data()?.name || planSnap.id, planSnap.data()),
        ...planSnap.data(),
      };
    }
  } catch (error) {
    console.warn('[TenantPricing] Could not read custom pricing plan:', error?.message);
  }

  if (PLANS[planName]) return { id: planName, name: planName, source: 'built-in', ...PLANS[planName] };
  return null;
}

export async function getAllPricingPlans(options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const staticPlans = PLAN_ORDER.map((planName) => ({
    id: planName,
    name: planName,
    source: 'built-in',
    ...normalizePlanConfig(planName, PLANS[planName]),
  }));

  try {
    const customSnapshot = await getDocs(collection(firestore, collections.pricingPlans));
    const customPlans = customSnapshot.docs.map((item) => {
      const data = item.data() || {};
      return {
        id: item.id,
        source: 'custom',
        ...normalizePlanConfig(data.name || item.id, data),
        ...data,
      };
    });
    const customByName = new Map(customPlans.map((plan) => [plan.name || plan.id, plan]));
    const mergedStatic = staticPlans.map((plan) => customByName.get(plan.name) || plan);
    const staticNames = new Set(staticPlans.map((plan) => plan.name));
    return [
      ...mergedStatic,
      ...customPlans.filter((plan) => !staticNames.has(plan.name)),
    ];
  } catch (error) {
    console.warn('[TenantPricing] Could not read custom pricing plans, using built-in plans:', error?.message);
    return staticPlans;
  }
}

export async function createPricingPlan(planName, planConfig = {}, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  if (!planName) return failureResult(FAILURE_REASONS.INVALID_PLAN);

  const normalizedPlan = normalizePlanConfig(planName, planConfig);
  await setDoc(doc(firestore, collections.pricingPlans, planName), {
    ...normalizedPlan,
    ...planConfig,
    name: planName,
    createdAt: planConfig.createdAt || new Date(),
    updatedAt: new Date(),
  }, { merge: true });
  return successResult({ planName, plan: { ...normalizedPlan, ...planConfig, name: planName } });
}

export async function updatePricingPlan(planName, updates = {}, options = {}) {
  return createPricingPlan(planName, updates, options);
}

export async function assignPlanToTenant(tenantIdOrDocId, planName, options = {}) {
  const plan = await getPricingPlan(planName, options);
  if (!plan) return failureResult(FAILURE_REASONS.INVALID_PLAN);
  const tenant = await getTenant(tenantIdOrDocId, options);
  if (!tenant) return failureResult(FAILURE_REASONS.INVALID_TENANT);

  const license = plan.source === 'built-in'
    ? buildPlanChangeLicense(tenant, planName, { actor: options.actor || 'system' })
    : {
        ...getLicenseForTenant(tenant, planName),
        ...buildLicenseForPlanConfig(planName, plan, { actor: options.actor || 'system' }),
        plan: planName,
        syncVersion: (tenant?.license?.syncVersion || 0) + 1,
      };
  return updateTenant(tenant.id, {
    plan: planName,
    planId: planName,
    monthlyCredits: license.currentCycle?.creditsTotal ?? tenant.monthlyCredits,
    usedCredits: license.currentCycle?.creditsUsed ?? tenant.usedCredits,
    remainingCredits: getTenantCreditBalance(license).remainingCredits,
    userLimit: license.userLimit ?? tenant.userLimit,
    purchasedCredits: license.rollover?.creditsAvailable ?? tenant.purchasedCredits ?? 0,
    allowedApps: Array.isArray(plan.allowedApps) ? plan.allowedApps : tenant.allowedApps,
    license,
  }, options);
}

function getActionCost(actionType, quantity = 1, options = {}) {
  const costs = getCreditActions(options);
  const unitCost = costs[actionType];
  if (!Number.isFinite(Number(unitCost))) return null;
  return Number(unitCost) * Math.max(1, Number(quantity) || 1);
}

export async function validateTenantCredits(tenantIdOrDocId, actionType, quantity = 1, options = {}) {
  const tenant = await getTenant(tenantIdOrDocId, options);
  if (!tenant) {
    return failureResult(FAILURE_REASONS.INVALID_TENANT);
  }
  if ((tenant.status || 'active') !== 'active') {
    return failureResult(FAILURE_REASONS.TENANT_INACTIVE);
  }

  const requiredCredits = getActionCost(actionType, quantity, options);
  if (requiredCredits === null) {
    return failureResult(FAILURE_REASONS.ACTION_NOT_ALLOWED, { actionType });
  }

  const license = getLicenseForTenant(tenant, options.defaultPlan || 'Professional');
  const effectiveLicense = isCycleExpired(license) ? renewLicenseCycle(license) : license;
  const balance = getTenantCreditBalance(effectiveLicense);

  if (balance.remainingCredits < requiredCredits) {
    return failureResult(FAILURE_REASONS.INSUFFICIENT_CREDITS, {
      requiredCredits,
      remainingCredits: balance.remainingCredits,
      plan: balance.plan,
    });
  }

  return successResult({
    requiredCredits,
    remainingCredits: balance.remainingCredits,
    plan: balance.plan,
    balance,
  });
}

export async function deductCreditsFromTenant(tenantIdOrDocId, actionType, quantity = 1, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const tenantDocId = await resolveTenantDocId(tenantIdOrDocId, options);
  if (!tenantDocId) return failureResult(FAILURE_REASONS.INVALID_TENANT);

  const requiredCredits = getActionCost(actionType, quantity, options);
  if (requiredCredits === null) {
    return failureResult(FAILURE_REASONS.ACTION_NOT_ALLOWED, { actionType });
  }

  const tenantRef = doc(firestore, collections.tenants, tenantDocId);

  try {
    const result = await runTransaction(firestore, async (transaction) => {
      const snap = await transaction.get(tenantRef);
      if (!snap.exists()) return failureResult(FAILURE_REASONS.INVALID_TENANT);

      const tenant = { id: snap.id, ...snap.data() };
      if ((tenant.status || 'active') !== 'active') {
        return failureResult(FAILURE_REASONS.TENANT_INACTIVE);
      }

      const now = new Date();
      const existingLicense = getLicenseForTenant(tenant, options.defaultPlan || 'Professional');
      const effectiveLicense = isCycleExpired(existingLicense)
        ? renewLicenseCycle(existingLicense, now)
        : existingLicense;
      const balance = getTenantCreditBalance(effectiveLicense);

      if (balance.remainingCredits < requiredCredits) {
        return failureResult(FAILURE_REASONS.INSUFFICIENT_CREDITS, {
          requiredCredits,
          remainingCredits: balance.remainingCredits,
          plan: balance.plan,
        });
      }

      const nextLicense = {
        ...effectiveLicense,
        currentCycle: { ...(effectiveLicense.currentCycle || {}) },
        rollover: { ...(effectiveLicense.rollover || {}) },
        lastSyncedAt: now.toISOString(),
        syncVersion: (effectiveLicense.syncVersion || 0) + 1,
      };
      const nextSourceVersion = getNextTenantSourceVersion(tenant);

      let remainingToSpend = requiredCredits;
      const rolloverLeft = computeRolloverRemaining(effectiveLicense);

      if (rolloverLeft > 0 && effectiveLicense.rollover?.isActive) {
        const fromRollover = Math.min(remainingToSpend, rolloverLeft);
        nextLicense.rollover.creditsUsed = (effectiveLicense.rollover.creditsUsed || 0) + fromRollover;
        remainingToSpend -= fromRollover;
      }

      if (remainingToSpend > 0) {
        nextLicense.currentCycle.creditsUsed = (effectiveLicense.currentCycle?.creditsUsed || 0) + remainingToSpend;
      }

      transaction.update(tenantRef, {
        license: nextLicense,
        sourceVersion: nextSourceVersion,
        updatedAt: now,
      });

      return successResult({
        tenantId: tenant.tenantId || tenantIdOrDocId,
        tenantDocId,
        actionType,
        quantity: Math.max(1, Number(quantity) || 1),
        requiredCredits,
        balanceBefore: balance.remainingCredits,
        remainingCredits: computeCreditsRemaining(nextLicense),
        license: nextLicense,
        sourceVersion: nextSourceVersion,
      });
    });
    if (result.success) {
      await recordCreditUsage({
        tenantId: result.tenantId,
        actionType,
        credits: result.requiredCredits,
        quantity: result.quantity,
        performedBy: options.actor || 'system',
        referenceId: options.referenceId || null,
        balanceBefore: result.balanceBefore,
        balanceAfter: result.remainingCredits,
        sourceVersion: result.sourceVersion,
        status: 'success',
        type: 'deduction',
      }, options).catch((error) => {
        console.warn('[TenantPricing] Could not record credit usage:', error?.message || error);
      });
    }
    return result;
  } catch (error) {
    return failureResult(error.code || 'UNKNOWN_ERROR', { message: error.message });
  }
}

export async function addCreditsToTenant(tenantIdOrDocId, creditsToAdd, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const tenantDocId = await resolveTenantDocId(tenantIdOrDocId, options);
  const amount = Math.floor(Number(creditsToAdd));

  if (!tenantDocId) return failureResult(FAILURE_REASONS.INVALID_TENANT);
  if (!Number.isFinite(amount) || amount <= 0) {
    return failureResult(FAILURE_REASONS.ACTION_NOT_ALLOWED, { message: 'Credit amount must be positive' });
  }

  const tenantRef = doc(firestore, collections.tenants, tenantDocId);
  const result = await runTransaction(firestore, async (transaction) => {
    const snap = await transaction.get(tenantRef);
    if (!snap.exists()) return failureResult(FAILURE_REASONS.INVALID_TENANT);

    const tenant = { id: snap.id, ...snap.data() };
    const existingLicense = getLicenseForTenant(tenant, options.defaultPlan || 'Professional');
    const rolloverExpiry = existingLicense?.rollover?.expiresAt ? new Date(existingLicense.rollover.expiresAt) : null;
    const rolloverActive = existingLicense?.rollover?.isActive
      && rolloverExpiry
      && rolloverExpiry.getTime() > Date.now();
    const nextRolloverAvailable = (existingLicense?.rollover?.creditsAvailable || 0) + amount;

    const expiryBase = existingLicense?.currentCycle?.cycleEnd
      ? new Date(existingLicense.currentCycle.cycleEnd)
      : new Date();
    const expiry = expiryBase.getTime() > Date.now() ? expiryBase : new Date();
    expiry.setDate(expiry.getDate() + ROLLOVER_WINDOW_DAYS);

    const license = {
      ...existingLicense,
      rollover: {
        ...(existingLicense?.rollover || {}),
        creditsAvailable: nextRolloverAvailable,
        creditsUsed: rolloverActive ? (existingLicense?.rollover?.creditsUsed || 0) : 0,
        expiresAt: rolloverActive ? existingLicense.rollover.expiresAt : expiry.toISOString(),
        isActive: true,
      },
      lastSyncedAt: nowIso(),
      syncVersion: (existingLicense?.syncVersion || 0) + 1,
      lastTopUpAt: nowIso(),
      lastTopUpCredits: amount,
      lastTopUpBy: options.actor || 'system',
    };
    const nextSourceVersion = getNextTenantSourceVersion(tenant);

    transaction.update(tenantRef, {
      plan: license.plan || getPlanNameFromTenant(tenant),
      license,
      monthlyCredits: license.currentCycle?.creditsTotal || 0,
      purchasedCredits: license.rollover?.creditsAvailable || 0,
      usedCredits: license.currentCycle?.creditsUsed || 0,
      remainingCredits: getTenantCreditBalance(license).remainingCredits,
      userLimit: license.userLimit || tenant.userLimit || 0,
      planId: license.plan || getPlanNameFromTenant(tenant),
      sourceVersion: nextSourceVersion,
      updatedAt: new Date(),
    });

    return successResult({
      tenantId: tenant.tenantId || tenantIdOrDocId,
      tenantDocId,
      creditsAdded: amount,
      balance: getTenantCreditBalance(license),
      remainingCredits: getTenantCreditBalance(license).remainingCredits,
      usedCredits: getTenantCreditBalance(license).usedCredits,
      license,
      sourceVersion: nextSourceVersion,
      tenant: {
        ...tenant,
        id: tenantDocId,
        tenantId: tenant.tenantId || tenantIdOrDocId,
        plan: license.plan || getPlanNameFromTenant(tenant),
        planId: license.plan || getPlanNameFromTenant(tenant),
        license,
        monthlyCredits: license.currentCycle?.creditsTotal || 0,
        purchasedCredits: license.rollover?.creditsAvailable || 0,
        usedCredits: license.currentCycle?.creditsUsed || 0,
        remainingCredits: getTenantCreditBalance(license).remainingCredits,
        userLimit: license.userLimit || tenant.userLimit || 0,
        sourceVersion: nextSourceVersion,
        updatedAt: new Date(),
      },
    });
  });

  if (result.success) {
    await recordCreditUsage({
      tenantId: result.tenantId,
      actionType: 'topUp',
      credits: amount,
      quantity: 1,
      performedBy: options.actor || 'system',
      status: 'success',
      type: 'addition',
    }, options).catch(() => {});
  }

  return result;
}

export async function recordCreditUsage(entry = {}, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const payload = {
    tenantId: entry.tenantId || '',
    actionType: entry.actionType || '',
    action: entry.action || entry.actionType || '',
    credits: Number(entry.credits || 0),
    creditsUsed: Number(entry.creditsUsed ?? (entry.type === 'addition' ? 0 : entry.credits) ?? 0),
    creditsAdded: Number(entry.creditsAdded ?? (entry.type === 'addition' ? entry.credits : 0) ?? 0),
    quantity: Number(entry.quantity || 1),
    performedBy: entry.performedBy || 'system',
    referenceId: entry.referenceId || null,
    balanceBefore: Number(entry.balanceBefore || 0),
    balanceAfter: Number(entry.balanceAfter || 0),
    sourceVersion: Number(entry.sourceVersion || 0),
    status: entry.status || 'success',
    type: entry.type || 'deduction',
    createdAt: entry.createdAt || new Date(),
  };

  const ref = await addDoc(collection(firestore, collections.usageHistory), payload);
  return { id: ref.id, ...payload };
}

export async function getCreditUsageHistory(options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const usageSnapshot = await getDocs(collection(firestore, collections.usageHistory));
  const tenantFilter = options.tenantId || '';
  return usageSnapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((item) => !tenantFilter || item.tenantId === tenantFilter)
    .sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
      return bTime - aTime;
    })
    .slice(0, options.limit || 50);
}

export async function getTenantUsage(tenantIdOrDocId, options = {}) {
  const tenant = await getTenant(tenantIdOrDocId, options);
  if (!tenant) return failureResult(FAILURE_REASONS.INVALID_TENANT);

  const balance = getTenantCreditBalance(tenant);
  const activeUsers = await getActiveTenantUserCount(tenant.tenantId || tenantIdOrDocId, options);

  return successResult({
    tenantId: tenant.tenantId || tenantIdOrDocId,
    plan: balance.plan,
    credits: balance,
    activeUsers,
    userLimit: balance.userLimit,
  });
}

export async function getActiveTenantUserCount(tenantId, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const usersSnapshot = await getDocs(query(collection(firestore, collections.users), where('tenantId', '==', tenantId)));
  return usersSnapshot.docs.filter((item) => (item.data()?.status || 'active') === 'active').length;
}

export async function getTenantUsers(tenantId, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const usersSnapshot = await getDocs(query(collection(firestore, collections.users), where('tenantId', '==', tenantId)));
  return usersSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function validateUserLimit(tenantIdOrDocId, options = {}) {
  const tenant = await getTenant(tenantIdOrDocId, options);
  if (!tenant) return failureResult(FAILURE_REASONS.INVALID_TENANT);

  const license = getLicenseForTenant(tenant, options.defaultPlan || 'Professional');
  const activeUsers = await getActiveTenantUserCount(tenant.tenantId || tenantIdOrDocId, options);
  const userLimit = computeActiveUserLimit(license);

  if (activeUsers >= userLimit) {
    return failureResult(FAILURE_REASONS.USER_LIMIT_REACHED, { activeUsers, userLimit });
  }

  return successResult({ activeUsers, userLimit });
}

const uniqueNonEmpty = (items = []) => [...new Set(items.filter(Boolean))];

export async function addTenantUser(tenantId, userPayload = {}, options = {}) {
  const limitResult = await validateUserLimit(tenantId, options);
  if (!limitResult.success) return limitResult;

  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const normalizedEmail = (userPayload.email || '').trim().toLowerCase();
  if (!normalizedEmail) return failureResult(FAILURE_REASONS.PERMISSION_DENIED, { message: 'User email is required' });

  const userRef = await addDoc(collection(firestore, collections.users), {
    email: normalizedEmail,
    tenantId,
    userName: userPayload.userName || userPayload.name || normalizedEmail,
    role: userPayload.role || 'User',
    assignedGroups: uniqueNonEmpty(userPayload.assignedGroups || []),
    status: userPayload.status || 'active',
    userId: '',
    createdAt: nowIso(),
    updatedAt: nowIso(),
  });
  await updateDoc(doc(firestore, collections.users, userRef.id), { userId: userRef.id });

  return successResult({ id: userRef.id, userId: userRef.id, tenantId, email: normalizedEmail });
}

export async function updateTenantUserRole(userId, role, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  if (!userId || !role) return failureResult(FAILURE_REASONS.PERMISSION_DENIED);

  await updateDoc(doc(firestore, collections.users, userId), {
    role,
    updatedAt: nowIso(),
  });

  return successResult({ userId, role });
}

export function checkTenantPermission(userData = {}, permission = 'tenant:read') {
  const role = String(userData.role || '').trim().toLowerCase();
  const isSuperadmin = role === 'superadmin' || role === 'super admin';
  const isTenantAdmin = role === 'admin' || role === 'tenant admin' || role === 'tenantadmin';

  const allowed = {
    'tenant:read': isSuperadmin || isTenantAdmin || role === 'user',
    'tenant:update': isSuperadmin || isTenantAdmin,
    'tenant:create': isSuperadmin,
    'tenant:disable': isSuperadmin,
    'pricing:assign': isSuperadmin,
    'credits:add': isSuperadmin,
    'credits:deduct': isSuperadmin || isTenantAdmin || role === 'user',
    'users:add': isSuperadmin || isTenantAdmin,
    'users:updateRole': isSuperadmin || isTenantAdmin,
  };

  return Boolean(allowed[permission]);
}

const isAdminPermission = (accessPermission = '') => {
  const value = String(accessPermission).trim().toLowerCase();
  return value === 'admin' || value === 'tenant admin' || value === 'tenantadmin';
};

const isAdminRole = (role = '') => {
  const value = String(role).trim().toLowerCase();
  return value === 'admin' || value === 'tenant admin' || value === 'tenantadmin';
};

async function getAdminGroupsForTenant(tenantId, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const groupsQuery = query(collection(firestore, collections.userGroups), where('tenantId', '==', tenantId));
  const groupsSnapshot = await getDocs(groupsQuery);
  return groupsSnapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((group) =>
      group.systemGroupType === 'tenant-admin' ||
      group.isSystem === true ||
      isAdminPermission(group.accessPermission)
    );
}

export async function ensureTenantAdminGroup(tenantId, tenantName = 'Tenant', options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const adminGroups = await getAdminGroupsForTenant(tenantId, options);
  if (adminGroups.length > 0) {
    const existing = adminGroups[0];
    if (existing.userGroupId !== existing.id) {
      await updateDoc(doc(firestore, collections.userGroups, existing.id), {
        userGroupId: existing.id,
        updatedAt: nowIso(),
      });
      return { ...existing, userGroupId: existing.id };
    }
    return existing;
  }

  const groupRef = await addDoc(collection(firestore, collections.userGroups), {
    groupName: `${tenantName} Admin`,
    accessPermission: 'Admin',
    tenantId,
    isSystem: true,
    systemGroupType: 'tenant-admin',
    createdAt: nowIso(),
    updatedAt: nowIso(),
  });
  await updateDoc(doc(firestore, collections.userGroups, groupRef.id), {
    userGroupId: groupRef.id,
    updatedAt: nowIso(),
  });

  return {
    id: groupRef.id,
    userGroupId: groupRef.id,
    groupName: `${tenantName} Admin`,
    accessPermission: 'Admin',
    tenantId,
    isSystem: true,
    systemGroupType: 'tenant-admin',
  };
}

async function upsertAdminUser({ tenantId, adminEmail, adminName, adminGroupId }, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const normalizedEmail = (adminEmail || '').trim().toLowerCase();
  const normalizedName = (adminName || '').trim();
  if (!tenantId || !normalizedEmail) {
    throw new Error('tenantId and adminEmail are required');
  }

  const usersQuery = query(collection(firestore, collections.users), where('email', '==', normalizedEmail));
  const usersSnapshot = await getDocs(usersQuery);
  const sameTenantUserDoc = usersSnapshot.docs.find(
    (docSnap) => (docSnap.data()?.tenantId || '') === tenantId
  );

  if (sameTenantUserDoc) {
    const existingDoc = sameTenantUserDoc;
    const existingData = existingDoc.data() || {};
    const assignedGroups = uniqueNonEmpty([
      ...(Array.isArray(existingData.assignedGroups) ? existingData.assignedGroups : []),
      adminGroupId,
    ]);

    const payload = {
      tenantId,
      email: normalizedEmail,
      userName: normalizedName || existingData.userName || normalizedEmail,
      role: 'Admin',
      assignedGroups,
      userId: existingData.userId || existingDoc.id,
      updatedAt: nowIso(),
    };

    await updateDoc(doc(firestore, collections.users, existingDoc.id), payload);
    return { id: existingDoc.id, ...existingData, ...payload };
  }

  const userResult = await addTenantUser(tenantId, {
    email: normalizedEmail,
    userName: normalizedName || normalizedEmail,
    role: 'Admin',
    assignedGroups: uniqueNonEmpty([adminGroupId]),
  }, options);

  if (!userResult.success) {
    throw new Error(userResult.reason || 'Unable to add tenant admin');
  }

  return {
    id: userResult.id,
    email: normalizedEmail,
    tenantId,
    userName: normalizedName || normalizedEmail,
    role: 'Admin',
    assignedGroups: uniqueNonEmpty([adminGroupId]),
    userId: userResult.userId,
  };
}

export async function addAdminToTenant(tenantId, adminEmail, adminName, options = {}) {
  const adminGroup = await ensureTenantAdminGroup(tenantId, options.tenantName, options);
  const user = await upsertAdminUser({
    tenantId,
    adminEmail,
    adminName,
    adminGroupId: adminGroup.id,
  }, options);

  await updateTenant(tenantId, {
    adminAssigned: true,
    lastAdminUpdatedAt: nowIso(),
  }, options);

  return {
    id: user.id,
    tenantId,
    adminEmail: user.email,
    adminName: user.userName,
    assignedGroups: user.assignedGroups || [],
    adminGroupId: adminGroup.id,
  };
}

export async function fetchTenantAdmins(tenantId, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const [usersSnapshot, adminGroups] = await Promise.all([
    getDocs(query(collection(firestore, collections.users), where('tenantId', '==', tenantId))),
    getAdminGroupsForTenant(tenantId, options),
  ]);

  const adminGroupIds = adminGroups.map((group) => group.id);

  return usersSnapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((user) => {
      const hasAdminRole = isAdminRole(user.role);
      const hasAdminGroup = (user.assignedGroups || []).some((groupId) => adminGroupIds.includes(groupId));
      return hasAdminRole || hasAdminGroup;
    })
    .map((user) => ({
      ...user,
      adminEmail: user.email || '',
      adminName: user.userName || '',
    }));
}

export async function removeAdminFromTenant(tenantId, adminId, replacement = {}, options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const userRef = doc(firestore, collections.users, adminId);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) return false;

  const userData = userSnap.data() || {};
  const replacementEmail = (replacement.email || '').trim().toLowerCase();
  const replacementName = (replacement.name || '').trim();
  const replacementTenantName = replacement.tenantName;

  if (!replacementEmail) {
    throw new Error('Replacement admin email is required before deleting this admin.');
  }

  if ((userData.email || '').toLowerCase() === replacementEmail) {
    throw new Error('Replacement admin must be different from the admin being deleted.');
  }

  await addAdminToTenant(tenantId, replacementEmail, replacementName || replacementEmail.split('@')[0], {
    ...options,
    tenantName: replacementTenantName,
  });

  const adminGroups = await getAdminGroupsForTenant(tenantId, options);
  const adminGroupIds = adminGroups.map((group) => group.id);
  const existingGroups = Array.isArray(userData.assignedGroups) ? userData.assignedGroups : [];
  const nextGroups = existingGroups.filter((groupId) => !adminGroupIds.includes(groupId));
  const payload = {
    assignedGroups: nextGroups,
    updatedAt: nowIso(),
  };

  if (isAdminRole(userData.role) && !nextGroups.some((groupId) => adminGroupIds.includes(groupId))) {
    payload.role = 'User';
  }

  await updateDoc(userRef, payload);
  await updateTenant(tenantId, {
    adminAssigned: nextGroups.length > 0,
    lastAdminUpdatedAt: nowIso(),
  }, options);
  return true;
}

export async function fetchUsersAndTenants(options = {}) {
  const firestore = getModuleDb(options);
  const collections = getCollections(options);
  const usersCollection = await getDocs(collection(firestore, collections.users));
  const tenantsCollection = await getDocs(collection(firestore, collections.tenants));

  return {
    users: usersCollection.docs.map((item) => ({ id: item.id, ...item.data() })),
    tenants: tenantsCollection.docs.map((item) => ({ id: item.id, ...item.data() })),
  };
}

export function decodeLicenseJWT(jwt) {
  try {
    if (!jwt) return null;
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload;
  } catch {
    return null;
  }
}

export function isJWTExpired(jwt) {
  const payload = decodeLicenseJWT(jwt);
  if (!payload || !payload.exp) return true;
  return Date.now() >= payload.exp * 1000;
}

export function isJWTNearExpiry(jwt) {
  const payload = decodeLicenseJWT(jwt);
  if (!payload || !payload.exp) return true;
  return (payload.exp * 1000 - Date.now()) < JWT_REFRESH_THRESHOLD;
}
